import type { HelpJonasSourceDocument } from "./components/jonas-source-panel.js";
import {
  IMAGE_DIALOG_REQUEST_EVENT,
  type HelpJonasImageDialog,
  type ImageDialogRequest,
} from "./components/jonas-image-dialog.js";

// Bootstraps the article-only embed view. In embed mode the server renders no
// chat host element and no chat config island, so nothing chat-related boots on
// its own. This mounts only the light-DOM article (adopted from the server SSR
// pane) plus the shared image dialog. It deliberately never creates the chat
// session store, never instantiates the chat API client, and never calls any
// chat endpoint. Article interaction (link clicks, table/video/image resize) is
// handled inside the source-document element itself: it only intercepts
// click-resize images and toggleBlock expanders, so every other link performs a
// normal browser navigation to the already server-rewritten embed=1+theme URL.
export function bootstrapEmbedMode(doc: Document, sourceUrl: string): void {
  const container =
    doc.querySelector<HTMLElement>("main[data-workspace-mode='embed']") ??
    doc.body;

  const adoptedHtml = consumePrerenderedSourceHtml(doc);
  if (adoptedHtml) {
    const article: HelpJonasSourceDocument = doc.createElement(
      "help-jonas-source-document",
    );
    article.html = adoptedHtml;
    article.sourceUrl = sourceUrl;
    container.appendChild(article);
  }

  // The image dialog is a sibling of the source document, not a child: the
  // source document sets `contain: layout`, which would become the containing
  // block for the dialog's position: fixed overlay and mis-center it.
  const dialog: HelpJonasImageDialog = doc.createElement(
    "help-jonas-image-dialog",
  );
  container.appendChild(dialog);

  // Click-resize images dispatch a bubbling, composed request for the shared
  // viewer. With no chat shell to host it in embed mode, route the request to
  // the dialog from the embed container instead.
  container.addEventListener(IMAGE_DIALOG_REQUEST_EVENT, (event) => {
    dialog.show((event as CustomEvent<ImageDialogRequest>).detail);
  });
}

// Mirrors HelpJonasChatApp.consumePrerenderedSourceHtml: adopt the server
// pre-rendered source pane HTML as the initial article, then remove its host so
// its article ids are not duplicated. Returns "" when there is no SSR pane.
function consumePrerenderedSourceHtml(doc: Document): string {
  const host = doc.querySelector("[data-ssr-source-pane]");
  if (!host) return "";
  const pane = host.querySelector("[data-article-source-pane]");
  const html = pane?.outerHTML ?? host.innerHTML;
  host.remove();
  return html;
}
